/**
 * Doublons : ce que l'écran en fait.
 *
 * Règle de Benjamin (2026-10-19) : « on a vite fait de créer plusieurs fois le même matériel ». À la
 * création (et à la correction), l'application dit qu'une fiche existe déjà et où elle est. Elle PRÉVIENT,
 * elle ne bloque pas : la décision reste humaine.
 *
 * Aucune règle de comparaison ici : c'est le serveur qui décide ce qui ressemble à quoi (marque et
 * numéro de série, raison sociale, nom de site…). L'écran ne fait que choisir QUAND interroger (pas à
 * chaque frappe, pas quand rien n'identifie la fiche) et COMMENT présenter la réponse.
 */

/** `MEME_OBJET` : on croit que c'est la même fiche (arrête une création non confirmée) ; `MEME_NUMERO` : simple information. */
export type NiveauDoublon = 'MEME_OBJET' | 'MEME_NUMERO';

/** Une fiche déjà existante qui ressemble à ce qu'on saisit. Les phrases sont prêtes à lire. */
export interface Doublon {
  readonly id: string;
  readonly niveau: NiveauDoublon;
  /** Pourquoi on la rapproche : « même marque et même numéro de série ». */
  readonly raison: string;
  /** Qui est-ce : « KAESER BSD72 n° 1223 ». */
  readonly titre: string;
  /** Où est-elle : « chez DUNOYER · site Atelier ». */
  readonly ou: string | null;
  /** « en service », « archivée le 19/10/2026 »… */
  readonly etat: string;
  readonly archive: boolean;
  readonly creeLe: string;
}

/** Code de la réponse HTTP 409 d'une création ou correction qui ressemble à une fiche existante. */
export const CODE_DOUBLON = 'DOUBLON_PROBABLE';

export type ObjetDoublon = 'machine' | 'client' | 'tiers' | 'site';

/** Ce qu'une erreur d'API doit porter pour être reconnue comme un doublon probable. */
interface ErreurPossible {
  readonly code?: string | undefined;
  readonly doublons?: readonly Doublon[] | undefined;
}

/**
 * Les fiches existantes quand l'erreur est un doublon probable (HTTP 409), `null` sinon.
 * Structurelle : elle ne dépend pas de la classe d'erreur du client d'API.
 */
export function doublonsDeLErreur(erreur: unknown): readonly Doublon[] | null {
  if (typeof erreur !== 'object' || erreur === null) return null;
  const e = erreur as ErreurPossible;
  if (e.code !== CODE_DOUBLON || !Array.isArray(e.doublons) || e.doublons.length === 0) return null;
  return e.doublons;
}

/** Paramètres d'adresse d'une consultation de doublons : les valeurs vides sont omises, les autres rognées. */
export function requeteDoublons(critere: Readonly<Record<string, string | undefined>>): string {
  const p = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(critere)) {
    const v = valeur?.trim();
    if (v) p.set(cle, v);
  }
  const q = p.toString();
  return q ? `?${q}` : '';
}

export function separer(doublons: readonly Doublon[]): {
  readonly memeObjet: readonly Doublon[];
  readonly memeNumero: readonly Doublon[];
} {
  return {
    memeObjet: doublons.filter((d) => d.niveau === 'MEME_OBJET'),
    memeNumero: doublons.filter((d) => d.niveau === 'MEME_NUMERO'),
  };
}

/** L'adresse de la fiche existante dans le bureau (les fiches archivées restent lisibles). */
export function lienFiche(objet: ObjetDoublon, id: string): string {
  switch (objet) {
    case 'machine':
      return `/machines/${id}`;
    case 'client':
    case 'tiers':
      return `/tiers/${id}`;
    case 'site':
      return `/sites/${id}`;
  }
}

const NOM: Record<ObjetDoublon, { readonly nom: string; readonly feminin: boolean; readonly pluriel: string }> = {
  machine: { nom: 'machine', feminin: true, pluriel: 'machines' },
  client: { nom: 'client', feminin: false, pluriel: 'clients' },
  tiers: { nom: 'tiers', feminin: false, pluriel: 'tiers' },
  site: { nom: 'site', feminin: false, pluriel: 'sites' },
};

/** Le titre de l'avertissement : une fiche, une fiche archivée, ou plusieurs. */
export function titreAvertissement(objet: ObjetDoublon, memeObjet: readonly Doublon[]): string {
  const { nom, feminin, pluriel } = NOM[objet];
  const ceCette = feminin ? 'Cette' : 'Ce';
  const celleCelui = feminin ? 'celle-ci' : 'celui-ci';
  if (memeObjet.length > 1) return `${memeObjet.length} ${pluriel} ressemblent à ${celleCelui}`;
  const [seule] = memeObjet;
  if (seule?.archive) return `${ceCette} ${nom} existe déjà, mais ${feminin ? 'elle est archivée' : 'il est archivé'}`;
  return `${ceCette} ${nom} existe déjà`;
}

/** La phrase d'information quand seul le numéro concorde (autre marque) : machines uniquement. */
export function phraseNumero(memeNumero: readonly Doublon[]): string {
  const [seule] = memeNumero;
  if (!seule) return '';
  if (memeNumero.length > 1) {
    return `${memeNumero.length} autres matériels portent ce numéro de série, d’une autre marque ou d’un autre modèle. Ce n’est pas la même machine : l’enregistrement reste possible.`;
  }
  const ou = seule.ou ? ` (${seule.ou})` : '';
  return `Un autre matériel porte ce numéro de série : ${seule.titre}${ou}. Marque ou modèle différents : l’enregistrement reste possible.`;
}

/**
 * Le libellé du bouton « créer quand même ». Il dit ce que l'utilisateur affirme : que ce n'est PAS la même
 * fiche. Quand seules des fiches archivées ressemblent, il dit « autre » (on ne la nie pas, on en crée une autre).
 */
export function libelleConfirmation(
  objet: ObjetDoublon,
  correction: boolean,
  memeObjet: readonly Doublon[],
): string {
  const { nom, feminin } = NOM[objet];
  const action = correction ? 'enregistrer quand même' : 'créer quand même';
  const seulementArchives = memeObjet.length > 0 && memeObjet.every((d) => d.archive);
  if (seulementArchives) return `C’est ${feminin ? 'une autre' : 'un autre'} ${nom}, ${action}`;
  return `Ce n’est pas ${feminin ? 'la même' : 'le même'}${objet === 'machine' ? '' : ` ${nom}`}, ${action}`;
}

/** La forme compacte d'une valeur : majuscules, sans séparateur (« BSD 72 » = « bsd72 »). Sert à repérer ce qui change vraiment. */
export function compacte(valeur: string | null | undefined): string {
  if (!valeur) return '';
  return valeur
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');
}

export interface IdentiteMachine {
  readonly serialNumber: string;
  readonly model: string;
  readonly clientDesignation: string;
  readonly brandPartyId: string;
  readonly siteId: string;
}

/**
 * Faut-il interroger les doublons de cette machine ? En création, dès qu'un numéro de série est saisi, ou une
 * désignation client avec un site. En correction, seulement si un champ qui IDENTIFIE la machine a changé :
 * ouvrir « Corriger » sur une machine qui a déjà un jumeau ne doit pas afficher un avertissement qu'on n'a pas
 * provoqué.
 */
export function doitInterrogerMachine(courant: IdentiteMachine, initial: IdentiteMachine | null): boolean {
  const aDeQuoi =
    compacte(courant.serialNumber) !== '' ||
    (compacte(courant.clientDesignation) !== '' && courant.siteId !== '');
  if (!aDeQuoi) return false;
  if (!initial) return true;
  return (
    compacte(courant.serialNumber) !== compacte(initial.serialNumber) ||
    compacte(courant.model) !== compacte(initial.model) ||
    compacte(courant.clientDesignation) !== compacte(initial.clientDesignation) ||
    courant.brandPartyId !== initial.brandPartyId ||
    courant.siteId !== initial.siteId
  );
}

export interface IdentiteTiers {
  readonly legalName: string;
  readonly legalIdentifier: string;
}

/** Même règle pour un client : au moins trois lettres de raison sociale, ou un identifiant légal ; en correction, seulement si l'un des deux a changé. */
export function doitInterrogerTiers(courant: IdentiteTiers, initial: IdentiteTiers | null): boolean {
  const aDeQuoi = compacte(courant.legalName).length >= 3 || compacte(courant.legalIdentifier) !== '';
  if (!aDeQuoi) return false;
  if (!initial) return true;
  return (
    compacte(courant.legalName) !== compacte(initial.legalName) ||
    compacte(courant.legalIdentifier) !== compacte(initial.legalIdentifier)
  );
}

export interface IdentiteSite {
  readonly customerId: string;
  readonly name: string;
  readonly addressLine1: string;
  readonly city: string;
}

/** Même règle pour un site : il faut un client et un nom (ou une adresse et une ville) ; en correction, seulement si le nom ou l'adresse ont changé. */
export function doitInterrogerSite(courant: IdentiteSite, initial: IdentiteSite | null): boolean {
  if (courant.customerId === '') return false;
  const aDeQuoi =
    compacte(courant.name) !== '' || (compacte(courant.addressLine1) !== '' && compacte(courant.city) !== '');
  if (!aDeQuoi) return false;
  if (!initial) return true;
  return (
    compacte(courant.name) !== compacte(initial.name) ||
    compacte(courant.addressLine1) !== compacte(initial.addressLine1) ||
    compacte(courant.city) !== compacte(initial.city)
  );
}
